'use client';

import { createContext, useContext, PropsWithChildren, useMemo, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { generateTabs, ITab } from '@/creditflowresearch/application/Deal/contexts/ProductsContext/utils/tabGenerator';
import { useHeaderMenuContext } from '@/creditflowresearch/application/HeaderMenu/HeaderMenuContext';
import Product, { AbsProduct } from '@/creditflowresearch/domain/Product';
import { useIsGuest } from '@/creditflowresearch/application/User/access';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import UserPlan from '@creditflowresearch/user-plan/UserPlan';
import { useAccessGrant } from '@/creditflowresearch/application/User/access/grants/useAccessGrant';
import { PATH_ONLY_GRANT_CONTEXT } from '@/creditflowresearch/application/User/access/grants/pagePatterns';

const useProducts = ({
  availableProductIds: initialAvailableProducts,
}: IProps) => {
  const router = useRouter();
  const { products } = useHeaderMenuContext();

  const productsQueryString = useMemo(() =>
    (router.query.products as string) || '',
  [router.query.products]);

  const urlProductIds = useMemo(() => {
    if (!products || !productsQueryString) {
      return [];
    }
    return Product.getIds(productsQueryString);
  }, [productsQueryString, products]);

  const activeProduct = useMemo(() => {
    return urlProductIds.length > 0 ? urlProductIds : initialAvailableProducts;
  }, [urlProductIds, initialAvailableProducts]);

  // TODO: remove when the European market opening period ends
  const isGuest = useIsGuest();
  // TODO: remove when the European market opening period ends
  const { plan } = useUserPlanContext();
  // TODO: remove when the European market opening period ends
  const isOpenAccessPage = useAccessGrant(PATH_ONLY_GRANT_CONTEXT);
  // TODO: remove when the European market opening period ends
  const isAbsHiddenFromVisitor = isOpenAccessPage
                                 && (isGuest || !UserPlan.isSatisfiedPremiumPlan(plan));

  const tabs = useMemo(() => {
    const generatedTabs = generateTabs(activeProduct);

    // TODO: remove when the European market opening period ends
    return isAbsHiddenFromVisitor
      ? generatedTabs.filter((tab) => !tab.productIds.includes(AbsProduct.PRODUCT_ID))
      : generatedTabs;
  }, [activeProduct, isAbsHiddenFromVisitor]);

  const getDefaultTab = (availableTabs: ITab[]): ITab | null => {
    if (availableTabs.length === 0) {
      return null;
    }
    return availableTabs.find((tab) => tab.productIds.includes(AbsProduct.PRODUCT_ID)) || availableTabs[0];
  };

  const defaultTab = getDefaultTab(tabs);
  const [selectedTabId, setSelectedTabId] = useState<string | null>(defaultTab?.id || null);

  useEffect(() => {
    if (!defaultTab) {
      return;
    }
    if (!selectedTabId || !tabs.some((tab) => tab.id === selectedTabId)) {
      setSelectedTabId(defaultTab.id);
    }
  }, [tabs, defaultTab, selectedTabId]);

  const selectedTab = useMemo(() => {
    return tabs.find((tab) => tab.id === selectedTabId) || defaultTab;
  }, [selectedTabId, tabs, defaultTab]);

  const onProductChange = (key: string) => {
    const tab = tabs.find((t) => t.id === key);
    if (tab) {
      setSelectedTabId(tab.id);
    }
  };

  return {
    tabs,
    activeProduct,
    selectedTab,
    onProductChange,
  };
};

type ProductContextType = ReturnType<typeof useProducts>;

export const ProductsContext = createContext<ProductContextType>(null);

export const useProductsContext = () => {
  const context = useContext(ProductsContext);
  if (!context) {
    throw new Error('useProductsContext must be used within the ProductsContext');
  }
  return context;
};

interface IProps {
  availableProductIds: string[],
}

const ProductsContextProvider = ({ children, availableProductIds }: PropsWithChildren<IProps>) => (
  <ProductsContext.Provider value={ useProducts({ availableProductIds }) }>
    { children }
  </ProductsContext.Provider>
);

export default ProductsContextProvider;
